<template>
  <div class="permission-presets">
    <h4>Permission Presets</h4>
    <div class="presets-container">
      <div class="roles-list" v-if="!embedded">
        <label class="label">Roles</label>
        <ul>
          <li
            v-for="role in roles"
            :key="role._id"
            :class="{ selected: selectedRole && selectedRole._id === role._id }"
            @click="selectRole(role)"
          >
            {{ role.rolename }}
          </li>
        </ul>
        <div class="actions">
          <b-button @click="createNewRole" type="is-primary">New Role</b-button>
        </div>
      </div>

      <div class="permissions-editor" v-if="selectedRole">
        <h5>Edit Preset for: {{ selectedRole.rolename }}</h5>
        <div class="modes">
          <div
            class="mode"
            v-if="isModeVisible(mode.key)"
            v-for="mode in modes"
            :key="mode.key"
          >
            <div class="mode-header">
              <h6>{{ mode.label }}</h6>
            </div>
            <div class="mode-items">
              <div
                v-for="section in getModeSections(mode)"
                :key="`${mode.key}-${section.key}`"
                class="permission-category"
              >
                <div class="permission-category-header">
                  <b-checkbox
                    :value="isSectionFullySelected(section)"
                    :disabled="section.readonly || isPowerAdminRole"
                    @input="toggleSection(section, $event)"
                  />
                  <p class="permission-category-title">{{ section.label }}</p>
                </div>
                <b-checkbox
                  v-for="item in section.items"
                  :key="item.key"
                  v-model="permissionSelections[item.key]"
                  :disabled="section.readonly || isPowerAdminRole"
                >
                  {{ item.label }}
                </b-checkbox>
              </div>
            </div>
          </div>
        </div>

        <div class="editor-actions" v-if="!embedded">
          <b-button @click="savePreset" type="is-success">Save</b-button>
          <b-button @click="cancelEdit">Cancel</b-button>
        </div>
      </div>

      <div class="empty" v-else-if="!embedded">
        <p>Select a role to edit its permission preset.</p>
      </div>
    </div>
  </div>
</template>

<script>
import PERMISSION_MODES from '../../config/permissionsConfig';

export default {
  name: 'permission-presets',
  props: {
    initialRole: {
      type: Object,
      default: null,
    },
    availableRoles: {
      type: Array,
      default: () => [],
    },
    activeTools: {
      type: Array,
      default: () => [],
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      roles: [],
      selectedRole: null,
      permissionSelections: {},
      modes: PERMISSION_MODES,
    };
  },
  computed: {
    isPowerAdminRole() {
      return (
        String(this.selectedRole?.rolename || '').toLowerCase() === 'poweradmin'
      );
    },
  },
  async created() {
    await this.loadRoles();
  },
  watch: {
    'initialRole._id': {
      handler: function initialRoleIdWatcher() {
        if (this.initialRole) {
          this.selectInitialRole();
        }
      },
    },
    activeTools: {
      deep: true,
      handler() {
        this.syncDisabledModeSelections();
      },
    },
    permissionSelections: {
      deep: true,
      handler() {
        if (!this.embedded || !this.selectedRole) return;

        this.$emit('permissions-change', this.buildPermissionsFromSelections());
      },
    },
  },
  methods: {
    async loadRoles() {
      if (this.availableRoles.length > 0) {
        this.roles = this.availableRoles;
      } else {
        await this.$store.dispatch('getUserRoles');
        this.roles = this.$store.state.userRoles || [];
      }

      const routeRoleId =
        this.$route && this.$route.query ? this.$route.query.roleId : null;

      if (routeRoleId) {
        const roleFromRoute = this.roles.find(
          (role) => role._id === routeRoleId
        );
        if (roleFromRoute) {
          this.selectRole(roleFromRoute);
          return;
        }
      }

      this.selectInitialRole();
    },
    selectInitialRole() {
      if (!this.initialRole) return;

      const initialRoleId = this.initialRole._id;
      const matchingRole = initialRoleId
        ? this.roles.find((role) => role._id === initialRoleId)
        : null;

      this.selectRole({
        ...this.initialRole,
        ...(matchingRole || {}),
        permissions:
          this.initialRole.permissions ||
          (matchingRole && matchingRole.permissions) ||
          [],
      });
    },
    selectRole(role) {
      this.selectedRole = JSON.parse(JSON.stringify(role));
      this.initSelectionsFromRole(this.selectedRole);
    },
    initSelectionsFromRole(role) {
      const selections = {};
      // initialize all keys to false
      this.modes.forEach((mode) => {
        this.getModeItems(mode).forEach((item) => {
          selections[item.key] = false;
        });
      });

      const normalize = (list) => {
        if (!list) return [];
        if (Array.isArray(list)) return list.map((l) => String(l).trim());
        if (typeof list === 'string')
          return list.split(',').map((l) => l.trim());
        return [];
      };

      const rolePermissions = normalize(role.permissions);
      const isPowerAdmin =
        String(role.rolename || '').toLowerCase() === 'poweradmin';

      this.modes.forEach((mode) => {
        this.getModeItems(mode).forEach((item) => {
          selections[item.key] =
            isPowerAdmin || rolePermissions.includes(item.key);
        });
      });

      this.permissionSelections = selections;
      this.syncDisabledModeSelections();
    },
    async savePreset() {
      if (!this.selectedRole) return;

      const permissions = this.buildPermissionsFromSelections();

      // update role object
      const updatedRole = { ...this.selectedRole, permissions };

      try {
        await this.$store.dispatch('updateUserRole', updatedRole);
        this.$buefy.toast.open({
          message: 'Permission preset saved',
          type: 'is-success',
        });
        await this.loadRoles();
      } catch (err) {
        console.error(err);
        this.$buefy.toast.open({
          message: 'Failed to save',
          type: 'is-danger',
        });
      }
    },
    cancelEdit() {
      if (this.embedded) {
        this.selectInitialRole();
        return;
      }

      this.selectedRole = null;
      this.permissionSelections = {};
    },
    buildPermissionsFromSelections() {
      const permissions = [];

      this.modes.forEach((mode) => {
        this.getModeItems(mode).forEach((item) => {
          if (this.permissionSelections[item.key]) {
            permissions.push(item.key);
          }
        });
      });

      return permissions;
    },
    normalizeList(list) {
      if (!list) return [];
      if (Array.isArray(list)) return list.map((item) => String(item).trim());
      if (typeof list === 'string') {
        return list.split(',').map((item) => item.trim());
      }
      return [];
    },
    getActiveTools() {
      const propTools = this.normalizeList(this.activeTools);
      if (propTools.length > 0) {
        return propTools;
      }

      return this.normalizeList(
        this.selectedRole && this.selectedRole.toollist
      );
    },
    isModeEnabled(modeKey) {
      if (modeKey === 'global') return true;
      return this.getActiveTools().includes(modeKey);
    },
    isModeVisible(modeKey) {
      return this.isModeEnabled(modeKey);
    },
    getModeItems(mode) {
      if (!mode) return [];

      if (Array.isArray(mode.items)) {
        return mode.items;
      }

      if (Array.isArray(mode.sections)) {
        return mode.sections.flatMap((section) => section.items || []);
      }

      return [];
    },
    getModeSections(mode) {
      // Use sections directly from mode if available (Admin mode)
      if (mode.sections && Array.isArray(mode.sections)) {
        return mode.sections.map((section) => ({
          key: section.title.toLowerCase().replace(/\s+/g, '-'),
          label: section.title,
          items: section.items || [],
          readonly: section.readonly || false,
        }));
      }

      // For other modes, wrap items in a default section
      const modeItems = this.getModeItems(mode);
      return [
        {
          key: 'default',
          label: 'Permissions',
          items: modeItems,
        },
      ];
    },
    syncDisabledModeSelections() {
      if (!this.selectedRole) return;

      const nextSelections = { ...this.permissionSelections };
      let hasChanges = false;

      this.modes.forEach((mode) => {
        if (this.isModeEnabled(mode.key)) return;

        this.getModeItems(mode).forEach((item) => {
          if (nextSelections[item.key]) {
            nextSelections[item.key] = false;
            hasChanges = true;
          }
        });
      });

      if (hasChanges) {
        this.permissionSelections = nextSelections;
      }
    },
    isModeFullySelected(mode) {
      const modeItems = this.getModeItems(mode);

      if (modeItems.length < 1) {
        return false;
      }

      return modeItems.every((item) => this.permissionSelections[item.key]);
    },
    toggleMode(mode, value) {
      if (!mode || !this.isModeEnabled(mode.key)) return;

      const nextValue = Boolean(value);
      this.getModeItems(mode).forEach((item) => {
        this.$set(this.permissionSelections, item.key, nextValue);
      });
    },
    isSectionFullySelected(section) {
      if (!section || !section.items || section.items.length < 1) {
        return false;
      }

      return section.items.every((item) => this.permissionSelections[item.key]);
    },
    toggleSection(section, value) {
      if (!section || !section.items) return;

      const nextValue = Boolean(value);
      section.items.forEach((item) => {
        this.$set(this.permissionSelections, item.key, nextValue);
      });
    },
    createNewRole() {
      // navigate to existing create role route
      this.$router.push({ name: 'create-role' });
    },
  },
};
</script>

<style scoped>
.permission-presets {
  padding: 20px;
  border-top: 1px solid #e5e5e5;
  margin-top: 32px;
}
.presets-container {
  display: flex;
  gap: 20px;
}
.roles-list {
  width: 220px;
  border-right: 1px solid #eee;
}
.roles-list ul {
  list-style: none;
  padding: 0;
}
.roles-list li {
  padding: 8px 10px;
  cursor: pointer;
}
.roles-list li.selected {
  background: #f2f2ff;
}
.permissions-editor {
  flex: 1;
}
.mode {
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 12px;
  background: #fff;
}

.mode-disabled {
  opacity: 0.45;
}
.modes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
.mode-items {
  display: flex;
  flex-direction: column;
}
.permission-category {
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.permission-category-header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.permission-category > :not(.permission-category-header) {
  margin-left: 20px;
}
.permission-category-title {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.mode-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.mode-select-all {
  font-size: 13px;
}
.editor-actions {
  margin-top: 16px;
  display: flex;
  gap: 8px;
}
.empty {
  color: #777;
}
</style>
